import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Search,
  User,
  Clock,
  ChevronLeft,
  ChevronRight,
  Zap,
  AlertTriangle,
  ChevronDown,
  ExternalLink,
  BotOff,
  Bot,
  UserPlus,
  X,
  MessageSquare,
  MessageSquareText,
  MessageCircle,
  Phone,
  PhoneOff,
  ClockAlert,
  UserCheck,
  Users,
  CircleDot,
  SendHorizonal,
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import apiService from './services/api';
import { Panel, LoadingSpinner, CallModal, SMSModal } from './components';

function formatResponseTime(hours) {
  if (hours == null) return null;
  if (hours < 1) {
    const mins = Math.round(hours * 60);
    return `${mins}min`;
  }
  if (hours < 24) {
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  }
  const d = Math.round(hours / 24);
  return `${d} day${d !== 1 ? 's' : ''}`;
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function daysAgo(dateStr) {
  if (!dateStr) return '';
  const eastern = { timeZone: 'America/New_York' };
  const d = new Date(dateStr);
  const now = new Date();
  const dStr = d.toLocaleDateString('en-CA', eastern);
  const nStr = now.toLocaleDateString('en-CA', eastern);
  const dDate = new Date(dStr + 'T00:00:00');
  const nDate = new Date(nStr + 'T00:00:00');
  const diff = Math.round((nDate - dDate) / (1000 * 60 * 60 * 24));
  if (diff === 0) return 'today';
  if (diff === 1) return '1 day ago';
  return `${diff} days ago`;
}

function isArchived(lead) {
  if (!lead.first_contact) return false;
  const age = (Date.now() - new Date(lead.first_contact).getTime()) / (1000 * 60 * 60 * 24);
  return age > 60 && !lead.converted && !['booked', 'lost', 'not_a_lead', 'archived'].includes(lead.latest_status);
}

function StatusDropdown({ lead, onStatusChange }) {
  const [open, setOpen] = useState(false);
  const [showReasonInput, setShowReasonInput] = useState(false);
  const [showSnoozeInput, setShowSnoozeInput] = useState(false);
  const [reason, setReason] = useState('');
  const [snoozeDate, setSnoozeDate] = useState('');
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const ref = useRef(null);
  const btnRef = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target) && btnRef.current && !btnRef.current.contains(e.target)) { setOpen(false); setShowReasonInput(false); setShowSnoozeInput(false); } };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleStatus = (e, status) => {
    e.stopPropagation();
    if (status === 'lost') {
      setShowReasonInput(true);
      return;
    }
    setOpen(false);
    onStatusChange(lead.person_id, status);
  };

  const submitLost = (e) => {
    e.stopPropagation();
    setOpen(false);
    setShowReasonInput(false);
    onStatusChange(lead.person_id, 'lost', reason);
    setReason('');
  };

  const getStatusInfo = () => {
    const ls = lead.latest_status;
    if (ls === 'not_a_lead') return { label: 'Not a Lead', cls: 'bg-gray-200 text-gray-500 line-through' };
    if (ls === 'booked' || lead.converted) return { label: 'Booked', cls: 'bg-green-100 text-green-800' };
    if (ls === 'lost') return { label: 'Lost', cls: 'bg-red-100 text-red-800' };
    if (lead.snooze_until) {
      const snoozeDate = new Date(lead.snooze_until + 'T12:00:00');
      const today = new Date(); today.setHours(12, 0, 0, 0);
      if (snoozeDate > today) {
        const snoozeDateStr = snoozeDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        return { label: `Snoozed → ${snoozeDateStr}`, cls: 'bg-purple-100 text-purple-700' };
      }
    }
    if (isArchived(lead)) return { label: 'Archived', cls: 'bg-gray-100 text-gray-400' };
    if (ls === 'follow_up') return { label: 'Follow Up', cls: 'bg-amber-100 text-amber-700' };
    if (ls === 'new' || !ls) return { label: 'New', cls: 'bg-blue-100 text-blue-800' };
    return { label: 'New', cls: 'bg-blue-100 text-blue-800' };
  };

  const info = getStatusInfo();

  return (
    <div className="relative" onClick={(e) => e.stopPropagation()}>
      <button
        ref={btnRef}
        onClick={(e) => {
          e.stopPropagation();
          if (!open && btnRef.current) {
            const rect = btnRef.current.getBoundingClientRect();
            setDropdownPos({ top: rect.bottom + 4, left: Math.max(8, rect.right - 176) });
          }
          setOpen(!open); setShowReasonInput(false);
        }}
        className={`text-xs font-medium px-1.5 py-0.5 rounded cursor-pointer hover:opacity-80 ${info.cls}`}
        title={lead.lost_reason ? `Lost: ${lead.lost_reason}` : info.label}
      >
        {info.label}
      </button>
      {lead.latest_status === 'lost' && lead.lost_reason && (
        <div className="text-[10px] text-red-400 mt-0.5 truncate max-w-[100px]" title={lead.lost_reason}>
          {lead.lost_reason}
        </div>
      )}
      {open && (
        <div ref={ref} className="fixed z-50 w-44 bg-white rounded-lg shadow-lg border border-gray-200 py-1" style={{ top: dropdownPos.top, left: dropdownPos.left }}>
          {showReasonInput ? (
            <div className="px-3 py-2">
              <p className="text-xs font-medium text-gray-700 mb-1">Why was this lead lost?</p>
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') submitLost(e); }}
                placeholder="e.g. No response, wrong area..."
                className="w-full px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-red-600 focus:outline-none"
                autoFocus
                onClick={(e) => e.stopPropagation()}
              />
              <button onClick={submitLost} className="mt-1.5 w-full text-xs bg-red-600 text-white rounded py-1 hover:bg-red-700">
                Mark Lost
              </button>
            </div>
          ) : showSnoozeInput ? (
            <div className="px-3 py-2">
              <p className="text-xs font-medium text-gray-700 mb-1">Reach back out on:</p>
              <input
                type="date"
                value={snoozeDate}
                onChange={(e) => setSnoozeDate(e.target.value)}
                min={new Date(Date.now() + 86400000).toISOString().split('T')[0]}
                className="w-full px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-purple-600 focus:outline-none"
                autoFocus
                onClick={(e) => e.stopPropagation()}
              />
              <div className="flex gap-1 mt-1.5">
                {[3, 7, 30].map(d => (
                  <button key={d} onClick={(e) => {
                    e.stopPropagation();
                    const dt = new Date(); dt.setDate(dt.getDate() + d);
                    setSnoozeDate(dt.toISOString().split('T')[0]);
                  }} className="flex-1 text-[10px] px-1 py-0.5 border rounded text-purple-700 hover:bg-purple-50">
                    {d}d
                  </button>
                ))}
              </div>
              <button onClick={(e) => {
                e.stopPropagation();
                if (!snoozeDate) return;
                setOpen(false);
                setShowSnoozeInput(false);
                onStatusChange(lead.person_id, 'snoozed', '', snoozeDate);
                setSnoozeDate('');
              }} disabled={!snoozeDate} className="mt-1.5 w-full text-xs bg-purple-600 text-white rounded py-1 hover:bg-purple-700 disabled:opacity-50">
                Snooze
              </button>
            </div>
          ) : (
            <>
              <button onClick={(e) => handleStatus(e, 'follow_up')} className="w-full text-left px-3 py-1.5 text-xs text-amber-700 hover:bg-amber-50">Follow Up</button>
              <button onClick={(e) => { e.stopPropagation(); setShowSnoozeInput(true); }} className="w-full text-left px-3 py-1.5 text-xs text-purple-700 hover:bg-purple-50">Snooze...</button>
              <button onClick={(e) => handleStatus(e, 'booked')} className="w-full text-left px-3 py-1.5 text-xs text-green-700 hover:bg-green-50">Booked</button>
              <button onClick={(e) => handleStatus(e, 'lost')} className="w-full text-left px-3 py-1.5 text-xs text-red-600 hover:bg-red-50">Lost...</button>
              <hr className="my-1 border-gray-100" />
              <button onClick={(e) => handleStatus(e, 'archived')} className="w-full text-left px-3 py-1.5 text-xs text-gray-400 hover:bg-gray-50">Archive</button>
              <button onClick={(e) => handleStatus(e, 'not_a_lead')} className="w-full text-left px-3 py-1.5 text-xs text-gray-400 hover:bg-gray-50">Not a Lead</button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function AssignDropdown({ lead, users, onAssign }) {
  const [open, setOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const ref = useRef(null);
  const btnRef = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target) && btnRef.current && !btnRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const openDropdown = (e) => {
    e.stopPropagation();
    const el = btnRef.current || e.currentTarget;
    if (el) {
      const rect = el.getBoundingClientRect();
      setDropdownPos({ top: rect.bottom + 4, left: Math.max(8, rect.right - 176) });
    }
    setOpen(!open);
  };

  const handleAssign = async (e, userId) => {
    e.stopPropagation();
    setOpen(false);
    onAssign(lead.person_id, userId);
  };

  const assigned = lead.assigned_to;

  return (
    <div className="relative" onClick={(e) => e.stopPropagation()}>
      {assigned ? (
        <div className="flex items-center gap-0.5">
          <button
            ref={btnRef}
            onClick={openDropdown}
            className="text-xs font-medium px-1.5 py-0.5 rounded bg-purple-100 text-purple-800 hover:bg-purple-200 transition-colors truncate max-w-[90px]"
            title={assigned.name}
          >
            {assigned.name.split(' ')[0]}
          </button>
          <button
            onClick={(e) => handleAssign(e, null)}
            className="text-gray-300 hover:text-red-500 transition-colors"
            title="Unassign"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      ) : (
        <button
          ref={btnRef}
          onClick={openDropdown}
          className="text-gray-300 hover:text-purple-600 transition-colors p-0.5 rounded hover:bg-purple-50"
          title="Assign lead"
        >
          <UserPlus className="w-3.5 h-3.5" />
        </button>
      )}
      {open && (
        <div ref={ref} className="fixed z-50 w-44 bg-white rounded-lg shadow-lg border border-gray-200 py-1 max-h-48 overflow-y-auto" style={{ top: dropdownPos.top, left: dropdownPos.left }}>
          {assigned && (
            <button
              onClick={(e) => handleAssign(e, null)}
              className="w-full text-left px-3 py-1.5 text-xs text-red-600 hover:bg-red-50 flex items-center gap-1.5"
            >
              <X className="w-3 h-3" /> Unassign
            </button>
          )}
          {users.map(u => (
            <button
              key={u.id}
              onClick={(e) => handleAssign(e, u.id)}
              className={`w-full text-left px-3 py-1.5 text-xs hover:bg-purple-50 flex items-center gap-1.5 ${
                assigned?.user_id === u.id ? 'bg-purple-50 text-purple-800 font-medium' : 'text-gray-700'
              }`}
            >
              <User className="w-3 h-3 text-gray-400" />
              {u.first_name} {u.last_name}
              {u.role && <span className="text-[10px] text-gray-400 ml-auto">{u.role}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function LeadsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(60);
  const [quickFilter, setQuickFilter] = useState(null);
  const [source, setSource] = useState('all');
  const [status, setStatus] = useState(urlParams.get('status') || 'all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState('arrived_desc');
  const [assignFilter, setAssignFilter] = useState(urlParams.get('assigned') || 'all');
  const [users, setUsers] = useState([]);
  const pageSize = 50;
  const searchTimer = useRef(null);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [expandedLead, setExpandedLead] = useState(null);
  const [interactions, setInteractions] = useState([]);

  // Fetch assignable team members once
  useEffect(() => {
    apiService.get('/amn/leads/assignable-users/').then(resp => {
      setUsers(resp || []);
    }).catch(() => {});
  }, []);

  // Debounce search
  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setDebouncedSearch(search);
    }, 400);
    return () => { if (searchTimer.current) clearTimeout(searchTimer.current); };
  }, [search]);

  const [weekOffset, setWeekOffset] = useState(0);

  const getISOWeek = useCallback((d) => {
    const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
    date.setUTCDate(date.getUTCDate() + 4 - (date.getUTCDay() || 7));
    const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
    return Math.ceil(((date - yearStart) / 86400000 + 1) / 7);
  }, []);

  const getWeekRange = useCallback((offset) => {
    const eastern = { timeZone: 'America/New_York' };
    const nowStr = new Date().toLocaleDateString('en-CA', eastern);
    const now = new Date(nowStr + 'T00:00:00');
    const day = now.getDay();
    const mon = new Date(now);
    mon.setDate(mon.getDate() - ((day + 6) % 7) + (offset * 7));
    const sun = new Date(mon);
    sun.setDate(sun.getDate() + 6);
    const weekNum = getISOWeek(mon);
    return {
      start: mon.toISOString().slice(0, 10),
      end: sun.toISOString().slice(0, 10),
      weekNum,
      monLabel: mon.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      sunLabel: sun.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    };
  }, [getISOWeek]);

  const getQuickDateRange = useCallback((filter) => {
    if (!filter) return null;
    const eastern = { timeZone: 'America/New_York' };
    const nowStr = new Date().toLocaleDateString('en-CA', eastern);
    const now = new Date(nowStr + 'T00:00:00');
    const day = now.getDay();

    if (filter === 'today') {
      return { start: nowStr, end: nowStr };
    }
    if (filter === 'yesterday') {
      const y = new Date(now); y.setDate(y.getDate() - 1);
      const ys = y.toISOString().slice(0, 10);
      return { start: ys, end: ys };
    }
    if (filter === 'this_week') {
      const mon = new Date(now); mon.setDate(mon.getDate() - ((day + 6) % 7));
      return { start: mon.toISOString().slice(0, 10), end: nowStr };
    }
    if (filter === 'last_week') {
      const mon = new Date(now); mon.setDate(mon.getDate() - ((day + 6) % 7) - 7);
      const sun = new Date(mon); sun.setDate(sun.getDate() + 6);
      return { start: mon.toISOString().slice(0, 10), end: sun.toISOString().slice(0, 10) };
    }
    if (filter === 'week') {
      return getWeekRange(weekOffset);
    }
    return null;
  }, [weekOffset, getWeekRange]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [days, source, status, debouncedSearch, assignFilter, quickFilter]);

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const params = { days, page, page_size: pageSize };
      if (source !== 'all') params.source = source;
      if (status !== 'all') params.status = status;
      if (debouncedSearch) params.search = debouncedSearch;
      if (assignFilter === 'mine') params.assigned_to = 'me';
      else if (assignFilter === 'unassigned') params.assigned_to = 'none';
      else if (assignFilter === 'assigned') params.assigned_to = 'any';
      else if (assignFilter.startsWith('user_')) params.assigned_to = assignFilter.split('_')[1];

      // Send date range filter server-side for proper pagination
      const range = getQuickDateRange(quickFilter);
      if (range) {
        params.activity_after = range.start;
        params.activity_before = range.end;
      }

      const result = await apiService.get('/amn/metrics/leads-by-person/', params);
      setData(result);
    } catch (err) {
      console.error('Error fetching leads:', err);
    } finally {
      setLoading(false);
    }
  }, [days, page, source, status, debouncedSearch, assignFilter, quickFilter, getQuickDateRange]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  const summary = data?.summary;
  const serverCount = data?.count || 0;
  const totalPages = Math.ceil(serverCount / pageSize);

  // Get current user ID for "My Leads" filter
  const currentUserId = useMemo(() => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) return null;
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.user_id;
    } catch { return null; }
  }, []);

  const [currentUserName, setCurrentUserName] = useState(null);
  useEffect(() => {
    apiService.get('/auth/profile/').then(data => {
      if (data?.first_name) setCurrentUserName(data.first_name);
    }).catch(() => {});
  }, []);

  // Client-side sort + assignment filter
  const sortedLeads = useMemo(() => {
    if (!data?.leads) return [];
    let leads = [...data.leads];

    // Date filtering is now server-side (activity_after/activity_before params)

    // Status and assignment filters are now server-side

    const [field, dir] = sort.split('_');
    const mul = dir === 'asc' ? 1 : -1;

    const nullsLast = (a, b, getter) => {
      const va = getter(a), vb = getter(b);
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      return va < vb ? -1 * mul : va > vb ? 1 * mul : 0;
    };

    switch (field) {
      case 'name':
        return leads.sort((a, b) => mul * ((a.name || '').localeCompare(b.name || '')));
      case 'source':
        return leads.sort((a, b) => mul * ((a.source || '').localeCompare(b.source || '')));
      case 'arrived':
        return leads.sort((a, b) => nullsLast(a, b, l => l.first_contact));
      case 'activity':
        return leads.sort((a, b) => nullsLast(a, b, l => l.last_interaction || l.first_contact));
      case 'appt':
        return leads.sort((a, b) => nullsLast(a, b, l => l.first_appointment_date));
      case 'response':
        return leads.sort((a, b) => nullsLast(a, b, l => l.response_time_hours));
      case 'touches':
        return leads.sort((a, b) => mul * ((a.touchpoints || 0) - (b.touchpoints || 0)));
      case 'revenue':
        return leads.sort((a, b) => mul * ((a.revenue || 0) - (b.revenue || 0)));
      default:
        return leads.sort((a, b) => new Date(b.first_contact) - new Date(a.first_contact));
    }
  }, [data?.leads, sort, status, assignFilter, currentUserId]);

  const toggleSort = (field) => {
    setSort(prev => {
      const [prevField, prevDir] = prev.split('_');
      if (prevField === field) return `${field}_${prevDir === 'desc' ? 'asc' : 'desc'}`;
      return `${field}_desc`;
    });
  };

  const SortHeader = ({ field, children, align = 'left' }) => {
    const [activeField, activeDir] = sort.split('_');
    const isActive = activeField === field;
    return (
      <th
        onClick={() => toggleSort(field)}
        className={`px-2 py-1.5 text-${align} text-xs font-medium uppercase cursor-pointer select-none hover:bg-gray-100 transition-colors ${isActive ? 'text-red-800' : 'text-gray-500'}`}
      >
        <span className="inline-flex items-center gap-0.5">
          {children}
          {isActive && <span className="text-[10px]">{activeDir === 'asc' ? '▲' : '▼'}</span>}
        </span>
      </th>
    );
  };

  const toggleLead = async (personId) => {
    if (expandedLead === personId) {
      setExpandedLead(null);
      setInteractions([]);
      return;
    }
    setExpandedLead(personId);
    setInteractions([]);
    try {
      const resp = await apiService.get(`/amn/interactions/`, { person: personId, ordering: '-date_time', page_size: 50 });
      setInteractions(resp.results || resp || []);
    } catch { setInteractions([]); }
  };

  const handleAssign = async (personId, userId) => {
    try {
      const result = await apiService.assignLead(personId, userId);
      setData(prev => ({
        ...prev,
        leads: prev.leads.map(l =>
          l.person_id === personId ? { ...l, assigned_to: result.assigned_to } : l
        ),
      }));
    } catch (err) {
      console.error('Failed to assign lead:', err);
    }
  };

  const handleStatusChange = async (personId, status, reason = '', snoozeUntil = null) => {
    try {
      await apiService.updateLeadStatus(personId, status, reason, snoozeUntil);
      setData(prev => ({
        ...prev,
        leads: prev.leads.map(l =>
          l.person_id === personId
            ? {
                ...l,
                latest_status: status === 'snoozed' ? 'follow_up' : status,
                lost_reason: status === 'lost' ? reason : l.lost_reason,
                snooze_until: status === 'snoozed' ? snoozeUntil : null,
              }
            : l
        ),
      }));
    } catch (err) {
      console.error('Failed to update lead status:', err);
    }
  };

  const [editingNote, setEditingNote] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [callTarget, setCallTarget] = useState(null);
  const [smsTarget, setSmsTarget] = useState(null);

  // Command center (for by_member breakdown only)
  const [cmdCenter, setCmdCenter] = useState(null);
  useEffect(() => {
    apiService.get('/amn/leads/command-center/').then(setCmdCenter).catch(() => {});
  }, []);

  // Status counts from the leads query itself (guarantees card = table match)
  const statusCounts = data?.status_counts || {};

  const handleSaveNote = async (personId) => {
    try {
      await apiService.updateLeadNote(personId, noteText);
      setData(prev => ({
        ...prev,
        leads: prev.leads.map(l =>
          l.person_id === personId ? { ...l, lead_note: noteText.trim() || null } : l
        ),
      }));
      setEditingNote(null);
    } catch (err) {
      console.error('Failed to save note:', err);
    }
  };

  const handleDeleteNote = async (personId) => {
    try {
      await apiService.updateLeadNote(personId, '');
      setData(prev => ({
        ...prev,
        leads: prev.leads.map(l =>
          l.person_id === personId ? { ...l, lead_note: null } : l
        ),
      }));
      setNoteText('');
      setEditingNote(null);
    } catch (err) {
      console.error('Failed to delete note:', err);
    }
  };

  const formatDateTime = (dt) => {
    if (!dt) return '';
    const d = new Date(dt);
    return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' });
  };

  const getTypeIcon = (type) => {
    if (type === 'phone_call' || type === 'call') return '📞';
    if (type === 'outgoing_call') return '📱';
    if (type === 'sms' || type === 'phone_text') return '💬';
    if (type === 'website_lead' || type === 'website_message') return '🌐';
    if (type === 'online_booking') return '📅';
    if (type?.includes('google_ads')) return '🔴';
    return '📋';
  };

  const getSourceBadge = (src) => {
    if (src === 'google_ads') return <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-red-100 text-red-800">Google Ads</span>;
    if (src === 'website') return <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-blue-100 text-blue-800">Website</span>;
    if (src === 'phone') return <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-purple-100 text-purple-800">Phone</span>;
    if (src === 'online_booking') return <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-green-100 text-green-800">Online Booking</span>;
    if (src === 'sms') return <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-teal-100 text-teal-800">SMS</span>;
    return <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">Other</span>;
  };

  const getStatusBadge = (lead) => {
    if (lead.latest_status === 'not_a_lead') return <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-gray-200 text-gray-500 line-through">Not a Lead</span>;
    if (lead.converted || lead.latest_status === 'booked') return <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-green-100 text-green-800">Booked</span>;
    if (lead.latest_status === 'lost') return <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-red-100 text-red-800">Lost</span>;
    if (lead.snooze_until) {
      const snoozeDate = new Date(lead.snooze_until + 'T12:00:00');
      const today = new Date(); today.setHours(12, 0, 0, 0);
      if (snoozeDate > today) return <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-purple-100 text-purple-700">Snoozed</span>;
    }
    if (isArchived(lead)) return <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-gray-100 text-gray-400">Archived</span>;
    if (lead.latest_status === 'follow_up') return <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">Follow Up</span>;
    return <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-blue-100 text-blue-800">New</span>;
  };

  const getResponseTimeDisplay = (hours) => {
    if (hours == null) return <span className="text-xs text-red-600 font-medium">No outreach</span>;
    const formatted = formatResponseTime(hours);
    let color = 'text-green-700';
    if (hours >= 1 && hours < 24) color = 'text-amber-600';
    if (hours >= 24) color = 'text-red-600';
    return <span className={`text-xs font-medium ${color}`}>{formatted}</span>;
  };

  const [oliviaSending, setOliviaSending] = useState(null);
  const [oliviaResult, setOliviaResult] = useState(null);

  const sendOliviaNow = async (e, lead) => {
    e.stopPropagation();
    if (oliviaSending) return;
    setOliviaSending(lead.person_id);
    setOliviaResult(null);
    try {
      const result = await apiService.post('/amn/olivia/send-now/', { person_id: lead.person_id });
      setOliviaResult({ personId: lead.person_id, ...result });
      if (result.status === 'sent') {
        setTimeout(() => setOliviaResult(null), 5000);
      }
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Failed';
      setOliviaResult({ personId: lead.person_id, status: 'error', reason: msg });
      setTimeout(() => setOliviaResult(null), 5000);
    } finally {
      setOliviaSending(null);
    }
  };

  const toggleOliviaExclude = async (e, lead) => {
    e.stopPropagation();
    const newVal = !lead.olivia_excluded;
    try {
      await apiService.toggleOliviaExclude(lead.person_id, newVal);
      setData(prev => ({
        ...prev,
        leads: prev.leads.map(l =>
          l.person_id === lead.person_id ? { ...l, olivia_excluded: newVal } : l
        ),
      }));
    } catch (err) {
      console.error('Failed to toggle Olivia exclude:', err);
    }
  };


  return (
    <div className="flex-1 min-h-0 bg-neutral-50 overflow-y-auto" style={{ fontFamily: 'Montserrat, system-ui, sans-serif' }}>
      <div className="p-3 pb-16">
        {/* Header */}
        <div className="mb-3">
          <h1 className="text-lg font-semibold text-gray-900 leading-none">Lead Intelligence</h1>
          <p className="text-xs text-gray-500 mt-0.5">Response time and conversion analytics</p>
        </div>

        {/* Action Center — counts from the same query as the table */}
        {data && (
          <div className="mb-3">
            <div className="flex items-center gap-2 mb-2">
              <CircleDot className="w-4 h-4 text-red-800" />
              <h2 className="text-sm font-semibold text-gray-900">Action Center</h2>
              {cmdCenter?.scoped === 'mine' && <span className="text-[10px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full font-medium">My Leads</span>}
            </div>
            <div className="grid grid-cols-3 gap-2">
              <button onClick={() => setStatus(status === 'new' ? 'all' : 'new')} className="text-left">
                <Panel className={`!p-2 border-l-2 border-l-blue-500 hover:bg-blue-50 transition-colors ${status === 'new' ? 'ring-2 ring-blue-400 bg-blue-50' : ''}`}>
                  <div className="flex items-center gap-1 mb-0.5">
                    <UserPlus className="w-3 h-3 text-blue-600" />
                    <p className="text-[10px] text-gray-500 font-medium">New</p>
                  </div>
                  <p className="text-xl font-bold text-blue-700">{statusCounts.new_count ?? '—'}</p>
                </Panel>
              </button>
              <button onClick={() => setStatus(status === 'follow_up' ? 'all' : 'follow_up')} className="text-left">
                <Panel className={`!p-2 border-l-2 border-l-amber-500 hover:bg-amber-50 transition-colors ${status === 'follow_up' ? 'ring-2 ring-amber-400 bg-amber-50' : ''}`}>
                  <div className="flex items-center gap-1 mb-0.5">
                    <ClockAlert className="w-3 h-3 text-amber-600" />
                    <p className="text-[10px] text-gray-500 font-medium">Follow Up</p>
                  </div>
                  <p className="text-xl font-bold text-amber-700">{statusCounts.follow_up_count ?? '—'}</p>
                </Panel>
              </button>
              <button onClick={() => { setAssignFilter(assignFilter === 'unassigned' ? 'all' : 'unassigned'); setStatus('all'); }} className="text-left">
                <Panel className={`!p-2 border-l-2 border-l-gray-400 hover:bg-gray-50 transition-colors ${assignFilter === 'unassigned' ? 'ring-2 ring-gray-400 bg-gray-50' : ''}`}>
                  <div className="flex items-center gap-1 mb-0.5">
                    <Users className="w-3 h-3 text-gray-500" />
                    <p className="text-[10px] text-gray-500 font-medium">Unassigned</p>
                  </div>
                  <p className="text-xl font-bold text-gray-600">{statusCounts.unassigned_count ?? '—'}</p>
                </Panel>
              </button>
            </div>

            {/* Per-member conversion breakdown */}
            {data?.by_member?.length > 0 && (
              <div className="mt-3 pt-2 border-t border-gray-100">
                <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Conversion by Team Member</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                  {data.by_member.map((m) => (
                    <button key={m.user_id} onClick={() => { setAssignFilter(prev => prev === `user_${m.user_id}` ? 'all' : `user_${m.user_id}`); setStatus('all'); }} className="text-left w-full">
                      <div className={`flex items-center gap-2.5 px-2.5 py-2 rounded border transition-colors ${
                        assignFilter === `user_${m.user_id}` ? 'border-red-300 bg-red-50' : 'border-gray-100 bg-white hover:border-gray-200'
                      }`}>
                        <div className="w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center text-[11px] font-bold text-gray-600 shrink-0">
                          {m.name?.[0] || '?'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-gray-900">{m.name}</p>
                          <p className="text-[10px] text-gray-500">{m.total_leads} leads</p>
                        </div>
                        <div className="flex gap-2 text-[10px] shrink-0 items-center">
                          <div className="text-right">
                            <p className="font-semibold text-green-700">{m.converted} booked</p>
                            <p className="text-gray-400">{m.conversion_rate}%</p>
                          </div>
                          {m.revenue > 0 && (
                            <div className="text-right pl-1.5 border-l border-gray-200">
                              <p className="font-semibold text-gray-700">${m.revenue.toLocaleString(undefined, {minimumFractionDigits: 0, maximumFractionDigits: 0})}</p>
                              <p className="text-gray-400">revenue</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-2 gap-2 mb-3">
            <Panel className="!p-2">
              <p className="text-xs text-gray-500">Total Leads</p>
              <p className="text-lg font-semibold text-gray-900">{summary.total_leads}</p>
            </Panel>
            <Panel className="!p-2">
              <p className="text-xs text-gray-500">Converted</p>
              <p className="text-lg font-semibold text-green-700">{summary.converted}</p>
              <p className="text-xs text-green-600">{summary.conversion_rate}%</p>
            </Panel>
          </div>
        )}

        {/* Quick Date Filters */}
        <div className="flex items-center gap-1.5 mb-2 flex-wrap">
          {[
            { key: null, label: 'All' },
            { key: 'today', label: 'Today' },
            { key: 'yesterday', label: 'Yesterday' },
            { key: 'this_week', label: 'This Week' },
            { key: 'last_week', label: 'Last Week' },
          ].map(f => (
            <button
              key={f.key || 'all'}
              onClick={() => { setQuickFilter(f.key); if (f.key !== 'week') setWeekOffset(0); }}
              className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                quickFilter === f.key
                  ? 'bg-red-800 text-white'
                  : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
              }`}
            >
              {f.label}
            </button>
          ))}
          <span className="text-gray-300 mx-0.5">|</span>
          <div className="flex items-center gap-0.5">
            <button
              onClick={() => { setWeekOffset(w => w - 1); setQuickFilter('week'); }}
              className="w-6 h-6 flex items-center justify-center rounded-full bg-white border border-gray-200 hover:bg-gray-50 text-gray-500 text-xs"
            >
              <ChevronLeft className="w-3 h-3" />
            </button>
            <button
              onClick={() => { setQuickFilter(quickFilter === 'week' ? null : 'week'); setWeekOffset(0); }}
              className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                quickFilter === 'week'
                  ? 'bg-red-800 text-white'
                  : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
              }`}
            >
              {quickFilter === 'week'
                ? `W${getWeekRange(weekOffset).weekNum} (${getWeekRange(weekOffset).monLabel} – ${getWeekRange(weekOffset).sunLabel})`
                : `Week #`
              }
            </button>
            <button
              onClick={() => { setWeekOffset(w => w + 1); setQuickFilter('week'); }}
              disabled={weekOffset >= 0}
              className="w-6 h-6 flex items-center justify-center rounded-full bg-white border border-gray-200 hover:bg-gray-50 text-gray-500 text-xs disabled:opacity-30"
            >
              <ChevronRight className="w-3 h-3" />
            </button>
          </div>
        </div>

        {/* Filters */}
        <Panel className="mb-3 !p-2">
          <div className="flex items-center gap-2 mb-2">
            <div className="relative flex-shrink-0" style={{ width: '180px' }}>
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 w-3.5 h-3.5" />
              <input
                type="text"
                placeholder="Search name, phone..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-7 pr-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-red-800 focus:outline-none"
              />
            </div>
            <div className="text-xs text-gray-400 flex items-center ml-auto">{sortedLeads.length} leads{assignFilter !== 'all' && ` (filtered)`}</div>
          </div>
          <div className="flex flex-wrap gap-1 mb-1.5">
            <span className="text-[10px] text-gray-400 font-medium self-center mr-0.5">Period</span>
            {[{ v: 30, l: '30d' }, { v: 60, l: '60d' }, { v: 90, l: '90d' }, { v: 180, l: '6mo' }, { v: 365, l: '1yr' }].map(d => (
              <button key={d.v} onClick={() => setDays(d.v)}
                className={`px-2 py-0.5 text-[11px] font-medium rounded transition-colors ${days === d.v ? 'bg-red-800 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                {d.l}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-1 mb-1.5">
            <span className="text-[10px] text-gray-400 font-medium self-center mr-0.5">Source</span>
            {[{ v: 'all', l: 'All' }, { v: 'phone', l: 'Phone' }, { v: 'website', l: 'Website' }, { v: 'online_booking', l: 'Booking' }, { v: 'google_ads', l: 'Google Ads' }, { v: 'sms', l: 'SMS' }].map(s => (
              <button key={s.v} onClick={() => setSource(s.v)}
                className={`px-2 py-0.5 text-[11px] font-medium rounded transition-colors ${source === s.v ? 'bg-red-800 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                {s.l}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-1 mb-1.5">
            <span className="text-[10px] text-gray-400 font-medium self-center mr-0.5">Assigned</span>
            {[
              { v: 'all', l: 'All' },
              { v: 'mine', l: 'My Leads' },
              { v: 'unassigned', l: 'Unassigned' },
              { v: 'assigned', l: 'Assigned' },
            ].map(a => (
              <button key={a.v} onClick={() => setAssignFilter(a.v)}
                className={`px-2 py-0.5 text-[11px] font-medium rounded transition-colors ${assignFilter === a.v ? 'bg-red-800 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                {a.l}
              </button>
            ))}
            {users.map(u => (
              <button key={u.id} onClick={() => setAssignFilter(prev => prev === `user_${u.id}` ? 'all' : `user_${u.id}`)}
                className={`px-2 py-0.5 text-[11px] font-medium rounded transition-colors ${assignFilter === `user_${u.id}` ? 'bg-red-800 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                {u.first_name}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-1">
            <span className="text-[10px] text-gray-400 font-medium self-center mr-0.5">Status</span>
            {[
              { v: 'all', l: 'All' },
              { v: 'new', l: 'New' },
              { v: 'follow_up', l: 'Follow Up' },
              { v: 'snoozed', l: 'Snoozed' },
              { v: 'booked', l: 'Booked' },
              { v: 'lost', l: 'Lost' },
              { v: 'not_a_lead', l: 'Not a Lead' },
              { v: 'archived', l: 'Archived' },
            ].map(s => (
              <button key={s.v} onClick={() => setStatus(s.v)}
                className={`px-2 py-0.5 text-[11px] font-medium rounded transition-colors ${status === s.v ? 'bg-red-800 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                {s.l}
              </button>
            ))}
          </div>
        </Panel>

        {/* Leads Table */}
        {loading ? (
          <Panel className="p-6"><LoadingSpinner /></Panel>
        ) : (
          <Panel noPadding>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <SortHeader field="name">Name & Contact</SortHeader>
                    <SortHeader field="source">Source</SortHeader>
                    <SortHeader field="arrived">Arrived</SortHeader>
                    <SortHeader field="activity">Last Activity</SortHeader>
                    <SortHeader field="response">Response Time</SortHeader>
                    <SortHeader field="touches" align="center">Touchpoints</SortHeader>
                    <th className="px-2 py-1.5 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-2 py-1.5 text-center text-xs font-medium text-gray-500 uppercase">Assigned</th>
                    <th className="px-2 py-1.5 text-center text-xs font-medium text-gray-500 uppercase">Notes</th>
                    <th className="px-2 py-1.5 text-center text-xs font-medium text-gray-500 uppercase" title="Olivia AI follow-up">Olivia</th>
                    <SortHeader field="revenue" align="right">Revenue</SortHeader>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {sortedLeads.map((lead) => (
                    <React.Fragment key={lead.person_id}>
                    <tr className={`hover:bg-gray-50 cursor-pointer ${expandedLead === lead.person_id ? 'bg-amber-50' : ''}`}
                        onClick={() => toggleLead(lead.person_id)}>
                      <td className="px-2 py-1.5">
                        <div className="flex items-center gap-1">
                          <ChevronDown className={`w-3 h-3 text-gray-400 transition-transform ${expandedLead === lead.person_id ? 'rotate-180' : ''}`} />
                          <div>
                            <div className="flex items-center gap-1">
                              <span className="text-xs font-semibold text-red-800">{lead.name}</span>
                              <button
                                onClick={(e) => { e.stopPropagation(); navigate(`/leads/${lead.person_id}`); }}
                                className="text-gray-400 hover:text-red-700 transition-colors"
                                title="View lead details"
                              >
                                <ExternalLink className="w-3 h-3" />
                              </button>
                              {lead.phone && (
                                <>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setCallTarget({ phone: lead.phone, name: lead.name, personId: lead.person_id });
                                    }}
                                    className="p-0.5 rounded bg-green-50 text-green-600 hover:bg-green-100 hover:text-green-700 transition-colors"
                                    title={`Call ${lead.phone}`}
                                  >
                                    <Phone className="w-3.5 h-3.5" />
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSmsTarget({ phone: lead.phone, name: lead.name, personId: lead.person_id });
                                    }}
                                    className="p-0.5 rounded bg-blue-50 text-blue-600 hover:bg-blue-100 hover:text-blue-700 transition-colors"
                                    title={`Text ${lead.phone}`}
                                  >
                                    <MessageCircle className="w-3.5 h-3.5" />
                                  </button>
                                </>
                              )}
                            </div>
                            {lead.phone && <div className="text-xs text-gray-500">{lead.phone}</div>}
                            {lead.email && <div className="text-xs text-gray-400">{lead.email}</div>}
                          </div>
                        </div>
                      </td>
                      <td className="px-2 py-1.5">
                        {getSourceBadge(lead.source)}
                        {lead.source === 'phone' && lead.answered_by && (
                          <div className="text-[10px] text-gray-400 mt-0.5">{lead.answered_by}</div>
                        )}
                      </td>
                      <td className="px-2 py-1.5">
                        <div className="text-xs text-gray-700 whitespace-nowrap">{formatDate(lead.first_contact)}</div>
                        <div className="text-xs text-gray-400">{daysAgo(lead.first_contact)}</div>
                      </td>
                      <td className="px-2 py-1.5">
                        {lead.last_interaction ? (
                          <>
                            <div className="text-xs text-gray-700 whitespace-nowrap">{formatDate(lead.last_interaction)}</div>
                            <div className="text-xs text-gray-400">{daysAgo(lead.last_interaction)}</div>
                          </>
                        ) : (
                          <span className="text-xs text-gray-300">&mdash;</span>
                        )}
                      </td>
                      <td className="px-2 py-1.5">{getResponseTimeDisplay(lead.response_time_hours)}</td>
                      <td className="px-2 py-1.5 text-center">
                        <span className="text-xs text-gray-700">{lead.inbound_count} in / {lead.outbound_count} out</span>
                      </td>
                      <td className="px-2 py-1.5 text-center">
                        <StatusDropdown lead={lead} onStatusChange={handleStatusChange} />
                      </td>
                      <td className="px-2 py-1.5 text-center">
                        <AssignDropdown lead={lead} users={users} onAssign={handleAssign} />
                      </td>
                      <td className="px-2 py-1.5 text-center">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingNote(lead.person_id);
                            setNoteText(lead.lead_note || '');
                          }}
                          className={`p-1 rounded transition-colors ${
                            lead.lead_note
                              ? 'text-amber-500 hover:text-amber-700 hover:bg-amber-50'
                              : 'text-gray-300 hover:text-gray-500 hover:bg-gray-50'
                          }`}
                          title={lead.lead_note || 'Add note'}
                        >
                          {lead.lead_note
                            ? <MessageSquareText className="w-4 h-4" />
                            : <MessageSquare className="w-4 h-4" />
                          }
                        </button>
                      </td>
                      <td className="px-2 py-1.5 text-center">
                        <div className="flex items-center justify-center gap-0.5">
                          <button
                            onClick={(e) => toggleOliviaExclude(e, lead)}
                            className={`p-1 rounded transition-colors ${
                              lead.olivia_excluded
                                ? 'text-red-400 hover:text-red-600 hover:bg-red-50'
                                : 'text-green-500 hover:text-green-700 hover:bg-green-50'
                            }`}
                            title={lead.olivia_excluded ? 'Excluded from Olivia — click to include' : 'Olivia can follow up — click to exclude'}
                          >
                            {lead.olivia_excluded ? <BotOff className="w-3.5 h-3.5" /> : <Bot className="w-3.5 h-3.5" />}
                          </button>
                          {!lead.olivia_excluded && !lead.converted && (
                            <button
                              onClick={(e) => sendOliviaNow(e, lead)}
                              disabled={oliviaSending === lead.person_id}
                              className={`p-1 rounded transition-colors ${
                                oliviaResult?.personId === lead.person_id && oliviaResult?.status === 'sent'
                                  ? 'text-green-600 bg-green-50'
                                  : oliviaResult?.personId === lead.person_id && (oliviaResult?.status === 'skipped' || oliviaResult?.status === 'error')
                                  ? 'text-red-400 bg-red-50'
                                  : 'text-purple-500 hover:text-purple-700 hover:bg-purple-50'
                              } disabled:opacity-50`}
                              title={
                                oliviaResult?.personId === lead.person_id
                                  ? oliviaResult?.status === 'sent' ? `Sent: ${oliviaResult.message?.slice(0, 60)}...` : oliviaResult?.reason || 'Skipped'
                                  : 'Send Olivia message now'
                              }
                            >
                              {oliviaSending === lead.person_id
                                ? <span className="w-3.5 h-3.5 block border-2 border-purple-300 border-t-purple-600 rounded-full animate-spin" />
                                : <SendHorizonal className="w-3.5 h-3.5" />
                              }
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="px-2 py-1.5 text-right text-xs font-medium">
                        {lead.converted && lead.revenue > 0 ? (
                          <span className="text-green-700">${lead.revenue.toLocaleString()}</span>
                        ) : (
                          <span className="text-gray-400">&mdash;</span>
                        )}
                      </td>
                    </tr>
                    {expandedLead === lead.person_id && (
                      <tr>
                        <td colSpan={11} className="px-4 py-2 bg-gray-50 border-b border-gray-200">
                          {interactions.length === 0 ? (
                            <p className="text-xs text-gray-400 py-1">Loading...</p>
                          ) : (
                            <div className="space-y-2">
                              {interactions.map(i => {
                                const isOlivia = ((i.metadata || {}).source || '').startsWith('olivia_');
                                const dir = (i.metadata || {}).direction;
                                const isOut = dir === 'outgoing' || i.type === 'outgoing_call';
                                const isIn = dir === 'incoming' || dir === 'inbound' || (!isOut && (i.type === 'phone_call' || i.type === 'call' || i.type?.includes('website') || i.type?.includes('google_ads')));
                                return (
                                <div key={i.id} className={`flex items-start gap-2 text-xs border-l-2 pl-3 py-0.5 ${isOlivia ? 'border-purple-300 bg-purple-50/50' : isOut ? 'border-green-300' : isIn ? 'border-blue-300' : 'border-gray-200'}`}>
                                  <span className="flex-shrink-0">{getTypeIcon(i.type)}</span>
                                  <span className="text-gray-400 w-28 flex-shrink-0">{formatDateTime(i.date_time)}</span>
                                  {isOlivia ? (
                                    <span className="px-1 py-0.5 rounded text-[10px] font-medium flex-shrink-0 bg-purple-100 text-purple-700">Olivia</span>
                                  ) : (
                                    <span className={`px-1 py-0.5 rounded text-[10px] font-medium flex-shrink-0 ${isOut ? 'bg-green-50 text-green-700' : isIn ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
                                      {isOut ? '↑ out' : isIn ? '↓ in' : i.type?.replace(/_/g, ' ')}
                                    </span>
                                  )}
                                  <span className="text-gray-600">{i.content || ''}</span>
                                </div>
                                );
                              })}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>

            {sortedLeads.length === 0 && !loading && (
              <div className="p-8 text-center text-gray-500">
                <User className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p className="text-xs">No leads found</p>
              </div>
            )}
          </Panel>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-2 flex items-center justify-between">
            <p className="text-xs text-gray-700">
              Page {page} of {totalPages} ({sortedLeads.length} shown of {serverCount})
            </p>
            <div className="flex gap-1">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium border border-gray-200 rounded bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-3 h-3" /> Previous
              </button>
              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page === totalPages}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium border border-gray-200 rounded bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Next <ChevronRight className="w-3 h-3" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Note Modal */}
      {editingNote && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setEditingNote(null)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <MessageSquareText className="w-5 h-5 text-amber-500" />
                <h3 className="text-sm font-semibold text-gray-900">
                  Lead Note — {sortedLeads.find(l => l.person_id === editingNote)?.name || 'Unknown'}
                </h3>
              </div>
              <button onClick={() => setEditingNote(null)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5">
              <textarea
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                placeholder="Add notes about this lead..."
                rows={4}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 focus:outline-none resize-none"
                autoFocus
              />
            </div>
            <div className="px-5 py-3 bg-gray-50 border-t border-gray-200 flex justify-end gap-2">
              <button
                onClick={() => handleDeleteNote(editingNote)}
                className="px-4 py-1.5 text-xs font-medium text-red-600 bg-white border border-red-200 rounded-lg hover:bg-red-50"
              >
                Delete
              </button>
              <button
                onClick={() => handleSaveNote(editingNote)}
                className="px-4 py-1.5 text-xs font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700"
              >
                Save Note
              </button>
            </div>
          </div>
        </div>
      )}

      <CallModal
        isOpen={!!callTarget}
        onClose={() => setCallTarget(null)}
        phoneNumber={callTarget?.phone}
        personName={callTarget?.name}
        personId={callTarget?.personId}
        apiService={apiService}
      />
      <SMSModal
        isOpen={!!smsTarget}
        onClose={() => setSmsTarget(null)}
        phoneNumber={smsTarget?.phone}
        personName={smsTarget?.name}
        personId={smsTarget?.personId}
        apiService={apiService}
        senderName={currentUserName}
      />
    </div>
  );
}

export default LeadsPage;
